import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Input, InputNumber } from 'antd';
import { toast } from 'react-toastify';
import axios from 'axios';
import config from '../../config';
import {
  NdoePage, NdoePageHeader, NdoeActionButton, NdoeImage,
  NdoeLoading, NdoeError, NdoeEmpty, filtreTexte,
} from '../../components/ndoe';
import useScanCode from './useScanCode';
import './inventaireComptage.css';

/*
  Écran de COMPTAGE — celui qu'on tient à la main, debout devant les cartons.

  Le geste est : je vois des paires → je les cherche → je saisis ce que je
  compte → je les mets de côté → je passe aux suivantes. Aucun ordre imposé,
  aucune liste à parcourir dans l'ordre.

  TROIS PARTIS PRIS

  1. COMPTAGE À L'AVEUGLE. On n'affiche NI le stock théorique, NI le total des
     autres compteurs. C'est ce qui rend un recomptage crédible : sinon on
     retrouve le chiffre attendu au lieu de compter. Le serveur ne les renvoie
     d'ailleurs pas.

  2. ON AJOUTE, ON NE CORRIGE PAS. Saisir 30 après 20 donne 50, pas 30. C'est
     le comptage réel : on trouve un deuxième carton du même modèle une heure
     plus tard. Pour se reprendre, on ANNULE un geste précis — il reste visible,
     barré.

  3. LE JOURNAL EST À PORTÉE. Chacun voit ses derniers gestes avec leur heure :
     c'est le seul garde-fou contre le double comptage d'un même carton, et il
     doit donc être visible sans changer d'écran.
*/

const InventaireComptage = () => {
  const DOMAIN = config.REACT_APP_SERVER_DOMAIN;
  const { id } = useParams();
  const navigate = useNavigate();

  const moi = useSelector((state) => state.user?.currentUser);
  const [session, setSession] = useState(null);
  const [articles, setArticles] = useState([]);
  const [mes, setMes] = useState([]);
  // Ma passe : 1 = comptage, 2 = recomptage, 3 = arbitrage. Le serveur la
  // déduit de mon affectation ; je dois la VOIR, parce qu'elle change le sens
  // de ce que je saisis (un recomptage remplace, il ne complète pas).
  const [maPasse, setMaPasse] = useState(1);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState('');

  const [q, setQ] = useState('');
  const [cible, setCible] = useState(null);   // article sélectionné
  const [qte, setQte] = useState(null);
  const [envoi, setEnvoi] = useState(false);
  const champQte = useRef(null);
  const scan = useScanCode();
  // Comptage EN RAFALE : on scanne, on saisit, on rescanne sans quitter la
  // camera. C'est le mode qui fait la difference sur un vrai inventaire.
  const [rafale, setRafale] = useState(false);

  const charger = useCallback(async () => {
    setChargement(true); setErreur('');
    try {
      const [s, l, m, a] = await Promise.all([
        axios.get(`${DOMAIN}/api/inventaires/${id}`),
        axios.get(`${DOMAIN}/api/inventaires/${id}/lignes`),
        axios.get(`${DOMAIN}/api/inventaires/${id}/mes-comptages`).catch(() => ({ data: [] })),
        axios.get(`${DOMAIN}/api/inventaires/${id}/affectations`).catch(() => ({ data: [] })),
      ]);
      setSession(s.data?.inventaire || s.data);
      setArticles(Array.isArray(l.data) ? l.data : (l.data?.lignes || []));
      setMes(Array.isArray(m.data) ? m.data : []);
      // Affecté à plusieurs passes ? Le serveur retient la plus haute — on
      // affiche exactement celle-là, pas une autre.
      const miennes = (Array.isArray(a.data) ? a.data : [])
        .filter((x) => Number(x.id_user) === Number(moi?.id))
        .map((x) => Number(x.passe));
      setMaPasse(miennes.length ? Math.max(...miennes) : 1);
    } catch (e) {
      setErreur(e?.response?.data?.error || 'Impossible d’ouvrir cet inventaire.');
    } finally {
      setChargement(false);
    }
  }, [DOMAIN, id, moi?.id]);

  useEffect(() => { charger(); }, [charger]);

  // Recherche sur tout ce qu'on a sous les yeux : la référence lue sur
  // l'étiquette, le modèle, la pointure sous ses DEUX formes (« 38 » et « P38 »
  // — c'est P38 qui est imprimé, mais 38 qu'on a en tête).
  const trouves = useMemo(() => {
    if (!q.trim()) return [];
    return articles
      .filter(filtreTexte(q, [
        'code_article', 'nom_produit', 'nom_marque', 'description',
        (a) => a.taille, (a) => (a.taille ? `P${a.taille}` : null),
      ]))
      .slice(0, 40);
  }, [articles, q]);

  const choisir = (a) => {
    setCible(a);
    setQte(null);
    // Le clavier numérique doit s'ouvrir sans un tap de plus : on compte debout.
    setTimeout(() => champQte.current?.focus(), 60);
  };

  // --- Scan ------------------------------------------------------------------
  // Le code lu est un `code_article`, unique par variante (vérifié : 1559 pour
  // 1559 variantes, aucune manquante). On le retrouve donc dans la liste déjà
  // chargée — le scan fonctionne sans réseau une fois l'inventaire ouvert.
  const parCode = useMemo(() => {
    const m = new Map();
    articles.forEach((a) => { if (a.code_article) m.set(String(a.code_article).trim().toUpperCase(), a); });
    return m;
  }, [articles]);

  const surCode = useCallback((code) => {
    const a = parCode.get(String(code).trim().toUpperCase());
    if (!a) {
      // Vibration longue = « ce n'est pas ça » : on doit le sentir sans regarder.
      if (navigator.vibrate) navigator.vibrate(300);
      toast.error(`Code ${code} : cet article n’est pas dans cet inventaire.`);
      return;
    }
    if (navigator.vibrate) navigator.vibrate(60);
    scan.arreter();
    choisir(a);
  }, [parCode, scan]);

  const lancerScan = async (enRafale) => {
    setRafale(!!enRafale);
    const r = await scan.scanner(surCode);
    if (!r.ok) {
      if (r.raison === 'permission') toast.error('Autorisez la caméra pour scanner.');
      else if (r.raison === 'indisponible') toast.info('Le scan n’est disponible que dans l’application Android.');
      else toast.error(r.message || 'Caméra indisponible.');
    }
  };

  const compter = async (source = 'recherche') => {
    if (!cible) return;
    const n = Number(qte);
    if (!Number.isInteger(n) || n < 0) { toast.error('Saisissez une quantité (0 accepté).'); return; }
    setEnvoi(true);
    try {
      const { data } = await axios.post(`${DOMAIN}/api/inventaires/${id}/comptages`, {
        id_varianteProduit: cible.id_varianteProduit,
        quantite: n,
        source,
      });
      // On confirme le TOTAL de l'article — pas le théorique, pas l'écart.
      toast.success(`+${n} · ${cible.nom_produit} P${cible.taille} — total compté : ${data.total_compte}`);
      setQte(null); setCible(null); setQ('');
      const { data: m } = await axios.get(`${DOMAIN}/api/inventaires/${id}/mes-comptages`);
      setMes(Array.isArray(m) ? m : []);
      // RAFALE : on rouvre la caméra tout de suite. Sur un vrai inventaire, ce
      // sont les allers-retours vers un bouton qui coûtent, pas la saisie.
      if (rafale && scan.disponible) setTimeout(() => scan.scanner(surCode), 250);
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Comptage refusé.');
    } finally {
      setEnvoi(false);
    }
  };

  const annuler = async (idComptage) => {
    try {
      await axios.delete(`${DOMAIN}/api/inventaires/comptages/${idComptage}`);
      toast.info('Comptage annulé.');
      const { data: m } = await axios.get(`${DOMAIN}/api/inventaires/${id}/mes-comptages`);
      setMes(Array.isArray(m) ? m : []);
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Annulation impossible.');
    }
  };

  const mesPairs = mes.filter((m) => !m.annule_le).reduce((s, m) => s + (Number(m.quantite) || 0), 0);

  if (chargement) return <NdoePage><NdoeLoading rows={5} /></NdoePage>;
  if (erreur) return <NdoePage><NdoeError text={erreur} onRetry={charger} /></NdoePage>;

  // Pendant le scan, la WebView est TRANSPARENTE et la caméra s'affiche
  // derrière : on ne peut donc rien montrer d'autre que la mire et les deux
  // commandes. Tout le reste de la page est masqué par la CSS (body.scan-actif).
  if (scan.actif) {
    return (
      <div className="ic-scanvue">
        <div className="ic-scanvue__mire" aria-hidden="true" />
        <p className="ic-scanvue__aide">
          Visez l’étiquette{rafale ? ' · mode rafale' : ''}
        </p>
        <div className="ic-scanvue__cmd">
          <button type="button" className={`ic-scanvue__torche${scan.torche ? ' is-on' : ''}`} onClick={scan.basculerTorche}>
            {scan.torche ? 'Éteindre' : 'Lampe'}
          </button>
          <button type="button" className="ic-scanvue__stop" onClick={() => { setRafale(false); scan.arreter(); }}>
            Arrêter
          </button>
        </div>
      </div>
    );
  }

  return (
    <NdoePage>
      <NdoePageHeader
        title="Compter"
        subtitle={`${session?.reference || `Inventaire #${id}`} · ${mesPairs} paire${mesPairs > 1 ? 's' : ''} comptée${mesPairs > 1 ? 's' : ''} par vous`}
        actions={
          <NdoeActionButton variant="secondary" onClick={() => navigate(`/inventaires/${id}`)}>
            Voir la session
          </NdoeActionButton>
        }
      />

      {/* La passe change le SENS de ce qu'on saisit : en recomptage, mon total
          remplace celui du premier passage au lieu de s'y ajouter. Silencieux,
          ce serait le meilleur moyen de fausser un inventaire. */}
      {maPasse > 1 && (
        <p className="ic-passe">
          <b>{maPasse === 2 ? 'Recomptage' : 'Arbitrage'} (passe {maPasse})</b> — vous repassez
          derrière un premier comptage. Ce que vous saisissez <b>remplacera</b> le total du
          passage précédent pour les articles que vous touchez. Comptez sans chercher à
          retrouver un chiffre : c’est le désaccord entre les deux passages qui est utile.
        </p>
      )}

      {/* --- Saisie ---------------------------------------------------------- */}
      <div className="ic-saisie">
        {!cible ? (
          <>
            {/* Le SCAN d'abord : c'est le geste le plus rapide, il doit être le
                plus gros. La recherche reste dessous pour les étiquettes
                abîmées ou illisibles. */}
            {scan.disponible && (
              <div className="ic-scan-barre">
                <button type="button" className="ic-scan" onClick={() => lancerScan(false)}>
                  <span className="ic-scan__ic" aria-hidden="true">▢</span>
                  Scanner une paire
                </button>
                <button
                  type="button"
                  className={`ic-scan__rafale${rafale ? ' is-actif' : ''}`}
                  onClick={() => lancerScan(true)}
                  title="Scanner, saisir, rescanner sans quitter la caméra"
                >
                  Rafale
                </button>
              </div>
            )}
            <Input.Search
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={scan.disponible ? 'Ou cherchez : référence, modèle, pointure…' : 'Référence, modèle, pointure (38 ou P38)…'}
              size="large"
              allowClear
              autoFocus={!scan.disponible}
            />
            {!q.trim() ? (
              <p className="ic-aide">
                Cherchez la paire que vous avez en main, saisissez ce que vous comptez,
                puis mettez-la de côté. Vous pouvez compter le même article plusieurs
                fois : les quantités <b>s’additionnent</b>.
              </p>
            ) : !trouves.length ? (
              <NdoeEmpty icon="🔍" title="Rien trouvé" text="Cet article n’est peut-être pas dans cet inventaire." />
            ) : (
              <ul className="ic-resultats">
                {trouves.map((a) => (
                  <li key={a.id_varianteProduit}>
                    <button type="button" className="ic-resultat" onClick={() => choisir(a)}>
                      <NdoeImage src={a.img} alt={a.nom_produit || 'Article'} size="sm" />
                      <div className="ic-resultat__corps">
                        <b>{a.nom_produit}</b>
                        <small>
                          {a.taille ? `P${a.taille}` : ''}
                          {a.description ? ` · ${a.description}` : ''}
                          {a.code_article ? ` · ${a.code_article}` : ''}
                        </small>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </>
        ) : (
          <div className="ic-cible">
            <div className="ic-cible__tete">
              <NdoeImage src={cible.img} alt={cible.nom_produit || 'Article'} size="lg" />
              <div>
                <b>{cible.nom_produit}</b>
                <small>
                  {cible.taille ? `P${cible.taille}` : ''}
                  {cible.description ? ` · ${cible.description}` : ''}
                </small>
                <small className="ic-ref">{cible.code_article}</small>
              </div>
            </div>
            <label className="ic-champ">
              <span>Combien de paires voyez-vous ?</span>
              <InputNumber
                ref={champQte}
                value={qte}
                onChange={setQte}
                min={0}
                max={99999}
                size="large"
                inputMode="numeric"
                style={{ width: '100%' }}
                onPressEnter={() => compter()}
              />
            </label>
            <div className="ic-actions">
              <NdoeActionButton variant="secondary" onClick={() => { setCible(null); setQte(null); }}>
                Annuler
              </NdoeActionButton>
              <NdoeActionButton variant="primary" disabled={envoi} onClick={() => compter()}>
                {envoi ? 'Enregistrement…' : 'Enregistrer ce comptage'}
              </NdoeActionButton>
            </div>
            <p className="ic-aide">
              Ce comptage <b>s’ajoute</b> à ce qui a déjà été compté sur cet article — par
              vous ou par quelqu’un d’autre.
            </p>
          </div>
        )}
      </div>

      {/* --- Mes gestes : le garde-fou contre le double comptage ------------- */}
      <h3 className="ic-section">Mes derniers comptages</h3>
      {!mes.length ? (
        <NdoeEmpty icon="📋" title="Rien encore" text="Vos comptages apparaîtront ici, avec leur heure." />
      ) : (
        <ul className="ic-journal">
          {mes.map((m) => (
            <li key={m.id_comptage} className={`ic-geste${m.annule_le ? ' is-annule' : ''}`}>
              <span className="ic-geste__qte">{m.quantite}</span>
              <div className="ic-geste__corps">
                <b>{m.nom_produit}{m.taille ? ` · P${m.taille}` : ''}</b>
                <small>{m.quand}{m.code_article ? ` · ${m.code_article}` : ''}{m.source !== 'manuel' ? ` · ${m.source}` : ''}</small>
              </div>
              {!m.annule_le && (
                <button type="button" className="ic-geste__annuler" onClick={() => annuler(m.id_comptage)}>
                  Annuler
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </NdoePage>
  );
};

export default InventaireComptage;
